import React, { useState } from 'react'
import { getDateString } from '..'
import { Card } from '../../../atoms'
import { RichText } from '../../helpers/react'
import { markdownParser } from '../../../utils/markdown-parser'
import { LenderIcon } from './LenderIcon'

type RatesListProps = {
  children: React.ReactNode
  footnotes: string
  dateString: string | null
}

type RatesListItemProps = {
  children: React.ReactNode
}

type Row = {
  type: 'th' | 'td'
  value: string
  marks: any
}

type RatesTableProps = {
  title?: string
  contents: Row[][]
  footnotes: any
  displayDate: boolean
  localDates: Date
  globalDates: Date
}

type Rate = {
  initialTermInYears: string
  ltv: string
  lenderName: string
  InitialMonthlyPayment: string
  InitialPayRate: string
  StandardVariableRate: string
  FeesTotal: string
  repExample: string
  Cashback: string
  displayCta: string
  tab: string | undefined
  rateType: string | undefined
  aprc: number | undefined
  overallInitialCost: number | undefined
}

const RatesList = ({ children, footnotes, dateString }: RatesListProps) => {
  return (
    <div className='RatesList'>
      <div className='RatesList__container'>
        <ul aria-label="Mortgage products' rates" className='RatesList__list'>
          {children}
        </ul>
      </div>
      {dateString && <div className='RatesTable__date'>Date {dateString}</div>}
      {footnotes && (
        <div className='RatesTable__footnotes'>
          <RichText content={footnotes} />
        </div>
      )}
    </div>
  )
}

const RatesListItem = ({ children }: RatesListItemProps) => (
  <li className='RatesTable__listItem'>{children}</li>
)

const RatesCard = ({
  product,
  clickout,
  selectedTab,
}: {
  product: Rate
  clickout: string
  selectedTab: number
}) => {
  const {
    ltv,
    initialTermInYears,
    lenderName,
    InitialMonthlyPayment: monthlyPayment,
    InitialPayRate: initialInterestRate,
    StandardVariableRate: variableRate,
    FeesTotal,
    repExample,
    Cashback,
    displayCta,
    rateType,
    aprc,
  } = product

  const formatNumberThousands = (value: number | string | null) => {
    if (value === null || isNaN(Number(value))) {
      return ''
    }

    const num = Number(value)
    const [integerPart, fractionalPart] = num.toString().split('.')
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    const formattedFractional = fractionalPart ? fractionalPart.padEnd(2, '0') : ''

    return formattedFractional ? `${formattedInteger}.${formattedFractional}` : formattedInteger
  }

  const cashBack = Number(Cashback)

  return (
    <div
      id={`panel-${selectedTab}`}
      className='RatesCard__wrapper'
      role='tabpanel'
      aria-labelledby={`tab-${selectedTab}`}
    >
      <Card variant='rounded'>
        <div className='RatesCard__headline'>
          <div className='RatesCard__lenderIcon'>
            <LenderIcon lenderName={lenderName ?? ''} />
          </div>
          <ul aria-label='Mortgage product highlights' className='InfoPill__List'>
            <li aria-label='Initial mortgage term' className='InfoPill'>
              {initialTermInYears} years
            </li>
            {rateType && (
              <li aria-label={rateType} className='InfoPill__blue'>
                {rateType} rate
              </li>
            )}
            {cashBack > 0 && (
              <li
                aria-label={`Cashback of £ ${formatNumberThousands(Cashback)}`}
                className='InfoPill__red'
              >
                £ {formatNumberThousands(Cashback)} cashback
              </li>
            )}
          </ul>
        </div>
        <div className='RateTableProduct__wrapper'>
          <ul aria-label='Mortgage product details' className='RateTableProduct__item_list'>
            {monthlyPayment && (
              <li className='RateTableProduct__item_other RateTableProduct__item_highlighted'>
                <span className='RateTableProduct__item_label Product__item_monthly_payment_value'>
                  Monthly repayment
                </span>
                <span className='RateTableProduct__item_value'>
                  £ {formatNumberThousands(monthlyPayment)}
                </span>
              </li>
            )}
            {ltv && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>Loan to value</span>
                <span className='RateTableProduct__item_value'>{ltv} %</span>
              </li>
            )}
            {initialInterestRate && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>Initial interest rate</span>
                <span className='RateTableProduct__item_value'>{initialInterestRate} %</span>
              </li>
            )}
            {variableRate && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>Variable rate</span>
                <span className='RateTableProduct__item_value'>{variableRate} %</span>
              </li>
            )}
            {aprc && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>APRC</span>
                <span className='RateTableProduct__item_value'>{aprc}%</span>
              </li>
            )}
            {FeesTotal && (
              <li className='RateTableProduct__item_other'>
                <span className='RateTableProduct__item_label'>Product fees</span>
                <span className='RateTableProduct__item_value'>
                  £ {formatNumberThousands(FeesTotal)}
                </span>
              </li>
            )}
          </ul>
          {displayCta === 'true' && (
            <div className='RateTableProduct__cta_wrapper'>
              <a className='RateTableProduct__cta' href={clickout}>
                Find my best mortgage deals
              </a>
            </div>
          )}
        </div>
        <div className='RateTableProduct__representative_example'>
          <span>
            <b>Representative example:</b>
          </span>
          <span dangerouslySetInnerHTML={{ __html: markdownParser(repExample ?? '') }} />
        </div>
      </Card>
    </div>
  )
}
const formatContents = (contents: Row[][]) => {
  let formattedContents: Rate[] = []
  let tableHeaders: string[] = []

  contents.forEach((row, rowIndex) => {
    let product: Rate = {
      initialTermInYears: '',
      ltv: '',
      lenderName: '',
      InitialMonthlyPayment: '',
      InitialPayRate: '',
      StandardVariableRate: '',
      FeesTotal: '',
      repExample: '',
      Cashback: '',
      displayCta: '',
      tab: '',
      rateType: '',
      aprc: undefined,
      overallInitialCost: undefined,
    }
    row.forEach((cell, cellIndex) => {
      if (cell.type === 'th') {
        tableHeaders.push(cell.value)
      } else {
        if (tableHeaders[cellIndex]) {
          product[tableHeaders[cellIndex]] = cell.value
        }
      }
    })

    if (rowIndex > 0) {
      formattedContents.push(product)
    }
  })

  return formattedContents
}

const RatesTable = (props: RatesTableProps) => {
  const { title, contents, footnotes, displayDate, localDates, globalDates } = props

  const dateString = displayDate ? getDateString(localDates, globalDates) : null

  const formattedContents: Rate[] = formatContents(contents)
  const tabs = Array.from(new Set(formattedContents.map((item) => item.tab)))

  const [selectedTab, setSelectedTab] = useState(0)

  const handleTabChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSelectedTab(Number(event.currentTarget.value))
  }

  return (
    <>
      <div className='RateTableWrapper'>
        {title && <div className='RateTable__title'>{title}</div>}
        {tabs.length > 1 && (
          <ul
            role='tablist'
            aria-label='Mortgage products'
            className='RateTableProduct__type_wrapper'
          >
            {tabs.map((tab, idx) => (
              <button
                key={tab}
                className={`RateTableProduct__tabButton ${
                  selectedTab === idx ? 'RateTableProduct__tabButton_active' : ''
                }`}
                role='tab'
                id={`tab-${idx}`}
                aria-label={`Mortgage products ${tab}`}
                aria-selected={selectedTab === idx}
                aria-controls={`panel-${idx}`}
                value={idx}
                onClick={handleTabChange}
              >
                {tab}
              </button>
            ))}
          </ul>
        )}
        <RatesList footnotes={footnotes} dateString={dateString}>
          {formattedContents
            .filter((row) => selectedTab === tabs.indexOf(row.tab))
            .map((row, rowIndex) => (
              <div key={`${selectedTab}-${rowIndex}`}>
                <RatesListItem key={rowIndex}>
                  <RatesCard
                    product={row}
                    clickout={clickouts[rowIndex]}
                    selectedTab={selectedTab}
                  />
                </RatesListItem>
              </div>
            ))}
        </RatesList>
      </div>
    </>
  )
}

export default RatesTable

const clickouts = [
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta1&sig=f2ee3400e740bdffb761483c82f0b6097d08220890e64ac9135a2f6fc7657372&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta2&sig=258df2a9dcb40824a40b6540c91a60739db7b404fa50bcc8332e2d824f4e1e76&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta3&sig=96123a4d0d76c7b4e781fd74dd5b2909c73874ea64d67d69f9a104cc47df30ad&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta4&sig=45a9dfb9a4251346f5eff73d0cdbc7dc24fe466fa91d76c4e9e6c1449b2d8417&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta5&sig=8248559d2d3412f6d67b8a80aa246a890607ede26cc470dd4fc07c46689d0e70&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta6&sig=457d567aab9b624f9e79ac0b82f79b0854a7ed3837af23110d56d3417f73f31f&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta7&sig=ac03b17ec03bf3a35b73ca5d0215cbc8de5d307703b3a677267cbb28696756fd&brand=uswitch',
  'https://www.uswitch.com/clk/mortgages/uswitch/mortgages?r=https%3A%2F%2Fuswitch.mojomortgages.com%2Fget-started%3Futm_source%3DPartner%26utm_medium%3DUswitch%26utm_term%3D%7B%7Bclick_ref%7D%7D%26utm_campaign%3Dseo_rates_table_cta8&sig=afcf827bd3c4e942eab5c8d5debc5a2f39a1a860c6248af282fe6a1ea92c2937&brand=uswitch',
]
